import { CSSProperties } from 'react';

import FeedbackIcon from 'assets/icons/feedback.svg';
import FeedbackActiveIcon from 'assets/icons/feedback-active.svg';
import NotificationsIcon from 'assets/icons/notifications.svg';
import NotificationsActiveIcon from 'assets/icons/notifications-active.svg';
import CalendarIcon from 'assets/icons/calendar.svg';
import CalendarActiveIcon from 'assets/icons/calendar-active.svg';
import CauseIcon from 'assets/icons/cause.svg';
import CauseActiveIcon from 'assets/icons/cause-active.svg';
import GrantsIcon from 'assets/icons/grants.svg';
import GrantsActiveIcon from 'assets/icons/grants-active.svg';
import ChaptersIcon from 'assets/icons/chapters.svg';
import ChaptersActiveIcon from 'assets/icons/chapters-active.svg';
import CogIcon from 'assets/icons/cog.svg';
import CogActiveIcon from 'assets/icons/cog-active.svg';
import CommunitiesIcon from 'assets/icons/communities.svg';
import CommunitiesActiveIcon from 'assets/icons/communities-active.svg';
import DonationIcon from 'assets/icons/donation.svg';
import DonationActiveIcon from 'assets/icons/donation-active.svg';
import HomeIcon from 'assets/icons/home.svg';
import HomeActiveIcon from 'assets/icons/home-active.svg';
import FormsIcon from 'assets/icons/forms.svg';
import FormsActiveIcon from 'assets/icons/forms-active.svg';
import VolunteersIcon from 'assets/icons/volunteers.svg';
import VolunteersActiveIcon from 'assets/icons/volunteers-active.svg';
import MegaPhone from 'assets/icons/megaphone.svg';
import MegaPhoneActive from 'assets/icons/megaphone-active.svg';
import Organizations from 'assets/icons/organizations.svg';
import OrganizationsActiveIcon from 'assets/icons/organizations-active.svg';
import Reports from 'assets/icons/reports.svg';
import ReportsActive from 'assets/icons/reports-active.svg';
import PayrollIcon from 'assets/icons/payroll.svg';
import PayrollActiveIcon from 'assets/icons/payroll-active.svg';
import DashboardIcon from 'assets/icons/view-dashboard-variant-outline.svg';
import DashboardActiveIcon from 'assets/icons/view-dashboard-variant-outline-active.svg';
import User from 'entities/user/model';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import Organization, { NonprofitEntityType } from 'entities/organization/model';
import InvoicesIcon from 'assets/icons/invoices.svg';
import InvoicesActiveIcon from 'assets/icons/invoices-active.svg';
import { truthy } from 'utils/truthy';

interface AppMenuLinkInterface {
  to: string;
  icon: string;
  activeIcon: string;
  title: string;
  privilegeNeeded: Privileges[];
  featureNeeded: Features[];
  imageStyle?: CSSProperties;
  sub?: AppMenuLinkInterface[];
  beta?: boolean;
  iconType?: 'svg' | 'mui';
}

const getAppMenuLinkCollection = (
  organization: Organization,
  user: User
): Array<AppMenuLinkInterface> => {
  const isCompany = organization?.isCompany();
  const isGovEntityOrMembershipOrg =
    organization?.nonprofitEntityType === NonprofitEntityType.governmentEntity ||
    organization?.nonprofitEntityType === NonprofitEntityType.memberAssociatedEntity;
  const hasCommunitiesOrTeamsAccess =
    user?.hasFeature(Features.COMMUNITIES) || user?.hasFeature(Features.TEAMS);
  const def: Array<AppMenuLinkInterface | false> = [
    {
      to: '/stats',
      icon: HomeIcon,
      activeIcon: HomeActiveIcon,
      title: 'Dashboard',
      privilegeNeeded: [Privileges.DASHBOARD_VIEW],
      featureNeeded: [Features.BASIC_DASHBOARDS],
    },
    {
      to: '/dashboards',
      icon: DashboardIcon,
      activeIcon: DashboardActiveIcon,
      title: 'Dashboards',
      privilegeNeeded: [Privileges.ADVANCED_DASHBOARD_VIEW],
      featureNeeded: [Features.ADVANCED_DASHBOARDS],
    },
    {
      to: '/reports',
      icon: Reports,
      activeIcon: ReportsActive,
      title: 'Reports',
      privilegeNeeded: [Privileges.REPORTS_VIEW, Privileges.REPORTS_VIEW_RESTRICTED],
      featureNeeded: [Features.REPORTS, Features.ADVANCED_REPORTING],
      imageStyle: {
        width: 32,
        margin: '8px 0',
      },
    },
    isCompany && {
      to: '/campaigns',
      icon: MegaPhone,
      activeIcon: MegaPhoneActive,
      title: 'Campaigns',
      privilegeNeeded: [Privileges.CAMPAIGN_MANAGE],
      featureNeeded: [],
      sub: [
        {
          to: '/challenges',
          iconType: 'mui',
          icon: 'LeaderboardOutlined',
          activeIcon: 'LeaderboardOutlined',
          title: 'Challenges',
          privilegeNeeded: [Privileges.CHALLENGES_VIEW],
          featureNeeded: [Features.CHALLENGES],
          beta: true,
        },
      ],
    },

    isCompany && {
      to: '/causes',
      icon: CauseIcon,
      activeIcon: CauseActiveIcon,
      title: 'Cause Areas',
      privilegeNeeded: [Privileges.CAUSE_MANAGE],
      featureNeeded: [],
    },
    {
      to: '/grants',
      icon: GrantsIcon,
      activeIcon: GrantsActiveIcon,
      title: 'Grants',
      privilegeNeeded: [Privileges.GRANTS_MANAGE],
      featureNeeded: [Features.GRANTS],
    },
    !isCompany &&
      !isGovEntityOrMembershipOrg && {
        to: '/chapters',
        icon: ChaptersIcon,
        activeIcon: ChaptersActiveIcon,
        title: 'Chapters',
        privilegeNeeded: [Privileges.CHAPTERS_MANAGE],
        featureNeeded: [],
      },
    hasCommunitiesOrTeamsAccess && {
      to: '/communities',
      icon: CommunitiesIcon,
      activeIcon: CommunitiesActiveIcon,
      title: 'Groups',
      privilegeNeeded: [Privileges.COMMUNITIES_EDIT],
      featureNeeded: [],
      beta: user?.hasFeature(Features.TEAMS),
    },
    {
      to: '/deeds/volunteer',
      icon: CalendarIcon,
      activeIcon: CalendarActiveIcon,
      title: 'Manage Deeds',
      privilegeNeeded: [
        Privileges.EVENT_MANAGE,
        Privileges.PROJECT_MANAGE,
        Privileges.FUNDRAISER_MANAGE,
        Privileges.BASE_EVENT_MANAGE,
      ],
      featureNeeded: [Features.VOLUNTEERING],
    },
    {
      to: '/volunteers',
      icon: VolunteersIcon,
      activeIcon: VolunteersActiveIcon,
      title: isCompany ? 'Employees' : 'Volunteers',
      privilegeNeeded: [Privileges.ORGANIZATION_MANAGE_USERS],
      featureNeeded: [Features.VOLUNTEERING],
    },
    {
      to: '/donations',
      icon: DonationIcon,
      activeIcon: DonationActiveIcon,
      title: 'Donations',
      privilegeNeeded: [Privileges.DONATION_VIEW],
      featureNeeded: [Features.DONATIONS],
      sub: [
        {
          to: '/donations/payroll',
          icon: PayrollIcon,
          activeIcon: PayrollActiveIcon,
          title: 'Payroll',
          privilegeNeeded: [Privileges.DONATION_PAYROLL_VIEW],
          featureNeeded: [Features.PAYROLL_API],
        },
        {
          to: '/donations/payroll',
          icon: PayrollIcon,
          activeIcon: PayrollActiveIcon,
          title: 'Payroll',
          privilegeNeeded: [Privileges.DONATION_PAYROLL_VIEW],
          featureNeeded: [Features.PAYROLL_FLAT_FILE],
        },
        Object.values(user?.organization?.disbursementConfiguration?.invoicing || {}).some(
          (v) => v
        ) && {
          to: '/invoices',
          icon: InvoicesIcon,
          activeIcon: InvoicesActiveIcon,
          title: 'Invoices',
          privilegeNeeded: [Privileges.RESTRICTED_INVOICING_VIEW],
          featureNeeded: [],
        },
      ].filter(truthy),
    },

    {
      to: '/forms',
      icon: FormsIcon,
      activeIcon: FormsActiveIcon,
      title: user?.hasFeature(Features.SURVEYS) ? 'Forms & Surveys' : 'Forms',
      privilegeNeeded: [Privileges.FORM_MANAGE, Privileges.SURVEY_MANAGE],
      featureNeeded: [Features.VOLUNTEERING],
    },
    isCompany
      ? {
          to: '/organizations',
          icon: Organizations,
          activeIcon: OrganizationsActiveIcon,
          title: 'Organizations',
          privilegeNeeded: [Privileges.ORGANIZATIONS_VIEW],
          featureNeeded: [],
        }
      : {
          to: '/companies',
          icon: Organizations,
          activeIcon: OrganizationsActiveIcon,
          title: 'Companies',
          privilegeNeeded: [Privileges.PARTNER_VIEW],
          featureNeeded: [],
        },
    {
      to: '/notifications',
      icon: NotificationsIcon,
      activeIcon: NotificationsActiveIcon,
      title: 'Notifications',
      privilegeNeeded: [Privileges.NOTIFCATIONS_ORGANIZATION_MANAGE],
      featureNeeded: [],
    },
    {
      to: '/feedback',
      icon: FeedbackIcon,
      activeIcon: FeedbackActiveIcon,
      title: 'Feedback',
      privilegeNeeded: [Privileges.PRODUCTBOARD_VIEW],
      featureNeeded: [Features.PRODUCTBOARD],
    },
    user?.isAdmin && {
      to: '/back-office',
      icon: 'Storefront',
      activeIcon: 'Storefront',
      title: 'Back Office',
      privilegeNeeded: [],
      featureNeeded: [],
      iconType: 'mui',
      sub: [
        {
          to: '/back-office/impact-ai',
          icon: 'Assistant',
          activeIcon: 'Assistant',
          title: 'ImpactAI',
          privilegeNeeded: [],
          featureNeeded: [],
          iconType: 'mui',
        },
      ],
    },
    {
      to: '/organization',
      icon: CogIcon,
      activeIcon: CogActiveIcon,
      title: 'Settings',
      privilegeNeeded: [
        Privileges.ORGANIZATION_MANAGE_SETTINGS,
        Privileges.ORGANIZATION_VIEW_ORGANIZERS,
      ],
      featureNeeded: [],
    },
  ];

  return def.filter(Boolean) as AppMenuLinkInterface[];
};

export { AppMenuLinkInterface, getAppMenuLinkCollection };
